import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const { lang, toggleLang, t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navigation = [
    { name: t('nav.home'), href: '/' },
    { name: t('nav.institutional'), href: '/conseil-institutionnel' },
    { name: t('nav.studio'), href: '/studio-digital' },
    { name: t('nav.team'), href: '/equipe' },
    { name: t('nav.contact'), href: '/contact' }
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-[#1B2A4A] shadow-lg' : 'bg-[#1B2A4A]'}`}>
      <nav className="px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 cursor-pointer group">
            <div className="flex flex-col">
              <h1 className="text-2xl font-bold text-white group-hover:text-[#D4A017] transition-colors leading-tight" style={{ fontFamily: 'Pacifico, cursive' }}>
                Ma@low
              </h1>
              <span className="text-white/90 font-medium text-xs tracking-wide">Consulting Services</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {navigation.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  isActive(item.href)
                    ? 'text-[#D4A017]'
                    : 'text-white hover:text-[#D4A017]'
                }`}
              >
                {item.name}
              </Link>
            ))}
            {/* Language Toggle */}
            <button
              onClick={toggleLang}
              className="flex items-center space-x-1 bg-white/10 rounded-full px-4 py-2 cursor-pointer hover:bg-white/20 transition-colors whitespace-nowrap"
              aria-label="Toggle language"
            >
              <span className={`text-sm font-medium transition-colors ${lang === 'fr' ? 'text-[#D4A017]' : 'text-white/60'}`}>
                FR
              </span>
              <span className="text-white/40 text-sm">|</span>
              <span className={`text-sm font-medium transition-colors ${lang === 'en' ? 'text-[#D4A017]' : 'text-white/60'}`}>
                EN
              </span>
            </button>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden text-white hover:text-[#D4A017] cursor-pointer transition-colors"
            aria-label="Toggle menu"
          >
            <i className={`ri-${isMenuOpen ? 'close' : 'menu'}-line text-3xl w-8 h-8 flex items-center justify-center`}></i>
          </button>
        </div>
      </nav>

      {/* Mobile menu overlay */}
      {isMenuOpen && (
        <div className="lg:hidden fixed inset-0 top-20 bg-[#1B2A4A] z-40">
          <div className="flex flex-col h-full px-6 py-8 space-y-6">
            {/* Mobile Language Toggle */}
            <button
              onClick={toggleLang}
              className="flex items-center justify-center space-x-2 bg-white/10 rounded-full px-6 py-3 cursor-pointer hover:bg-white/20 transition-colors whitespace-nowrap"
              aria-label="Toggle language"
            >
              <span className={`text-base font-medium transition-colors ${lang === 'fr' ? 'text-[#D4A017]' : 'text-white/60'}`}>
                FR
              </span>
              <span className="text-white/40">|</span>
              <span className={`text-base font-medium transition-colors ${lang === 'en' ? 'text-[#D4A017]' : 'text-white/60'}`}>
                EN
              </span>
            </button>

            {navigation.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                onClick={() => setIsMenuOpen(false)}
                className={`text-lg font-medium transition-colors cursor-pointer ${
                  isActive(item.href)
                    ? 'text-[#D4A017]'
                    : 'text-white hover:text-[#D4A017]'
                }`}
              >
                {item.name}
              </Link>
            ))}
            <Link
              to="/contact"
              onClick={() => setIsMenuOpen(false)}
              className="bg-[#D4A017] text-white px-6 py-3 rounded-md font-medium hover:bg-[#B8860B] transition-colors cursor-pointer text-center whitespace-nowrap"
            >
              {t('nav.contactUs')}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;